import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from '@mui/material'
import { FC } from 'react'
import { Divider, Icons, ItemImageArea } from 'utoniq-ui'

import { TradeBoxFragment, User } from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useTranslation } from '@/core'
import { usePrivateAction } from '@/lib/privateAction'

import { TradeTag } from '../TradeTag'

export type TradeBoxProps = {
  fullWidth?: boolean
  imgSize?: 'x-small' | 'small'
  href: string
  trades: TradeBoxFragment
  myUserId: string
}

export const TradeBox: FC<TradeBoxProps> = ({
  trades,
  fullWidth = false,
  imgSize = 'small',
  href,
  myUserId,
}) => {
  const { t } = useTranslation('myTradeDetail')
  const { handleLoginModal } = usePrivateAction()

  /**
   * 自分が関連するトレードかどうか
   */
  const isMyTrade =
    myUserId === trades.request.requestedByUser?.id ||
    myUserId === trades.candidates?.[0]?.user?.id

  return (
    <Box sx={{ pt: 4 }}>
      <Card
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'visible',
          width: fullWidth ? '100%' : 315,
          borderRadius: 2,
        }}
      >
        <TradeTag
          status={trades.status}
          sx={{ position: 'absolute', top: -10, left: 20, zIndex: 2 }}
        />
        <CardActionArea
          href={href}
          onClick={handleLoginModal()}
          sx={{ borderRadius: 2 }}
        >
          <CardContent>
            <Stack
              direction="row"
              flex={1}
              alignItems="center"
              spacing={2}
              sx={{ pb: 4 }}
            >
              <AvatarWithName user={trades.request.requestedByUser ?? null} />
              <Icons.TradeArrow
                sx={{ color: 'grey.200' }}
                status={trades.status}
              />
              <AvatarWithName
                user={trades.candidates[0].user ?? null}
                reverse
              />
            </Stack>
            <Stack direction="row" flex={1} spacing={2}>
              <ItemImageArea
                src={
                  trades.request?.providableItems[0]?.item?.thumbnail?.front
                    ?.mediumUri ?? ''
                }
                size={imgSize}
                alignItems="flex-start"
              />
              <Stack
                flex={1}
                justifyContent="space-between"
                sx={{
                  maxWidth: `calc(100% - ${imgSize === 'small' ? 136 : 112}px)`,
                }}
              >
                <Stack sx={{ mr: 4 }}>
                  <Stack>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        ...styleSets.lineCamp(1),
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                      }}
                    >
                      {trades.request.providableItems[0].item.title}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ pt: 1 }}
                  >
                    <Typography variant="overline" color="text.secondary">
                      {
                        trades.request.providableItems[0].itemSerial
                          .formattedSerialNumber
                      }
                    </Typography>
                    {
                      /**
                       * @description
                       * 自分が関わるトレードの場合、トレード元・トレード先アイテムの自分の保持枚数を表示する
                       */
                      isMyTrade && (
                        <>
                          <Divider orientation="vertical" sx={{ height: 10 }} />
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Icons.OwnedItems fontSize="xSmall" />
                            <Typography
                              variant="overline"
                              color="text.secondary"
                            >
                              {trades.request.providableItems?.[0].item
                                ?.ownedCount
                                ? t('ownedCount', {
                                    ownedCount:
                                      trades.request.providableItems?.[0].item
                                        ?.ownedCount,
                                  })
                                : t('notOwned')}
                            </Typography>
                          </Stack>
                        </>
                      )
                    }
                  </Stack>
                </Stack>
                <Stack sx={{ ml: 4 }}>
                  <Typography
                    variant="subtitle2"
                    align="right"
                    sx={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      maxWidth: '100%',
                    }}
                  >
                    {trades.candidates[0].candidateItems[0].item.title}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="flex-end"
                    sx={{ pt: 1 }}
                  >
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{
                        ...styleSets.lineCamp(1),
                        maxWidth: '100%',
                      }}
                    >
                      {
                        trades.candidates[0].candidateItems[0].itemSerial
                          .formattedSerialNumber
                      }
                    </Typography>
                    {
                      /**
                       * @description
                       * 自分が関わるトレードの場合、トレード元・トレード先アイテムの自分の保持枚数を表示する
                       */
                      isMyTrade && (
                        <>
                          <Divider orientation="vertical" sx={{ height: 10 }} />
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Icons.OwnedItems fontSize="xSmall" />
                            <Typography
                              variant="overline"
                              color="text.secondary"
                            >
                              {trades?.candidates[0]?.candidateItems?.[0]?.item
                                ?.ownedCount
                                ? t('ownedCount', {
                                    ownedCount:
                                      trades?.candidates[0]?.candidateItems?.[0]
                                        ?.item?.ownedCount,
                                  })
                                : t('notOwned')}
                            </Typography>
                          </Stack>
                        </>
                      )
                    }
                  </Stack>
                </Stack>
              </Stack>
              <ItemImageArea
                src={
                  trades.candidates[0].candidateItems[0]?.item?.thumbnail?.front
                    ?.mediumUri ?? ''
                }
                size={imgSize}
                alignItems="flex-end"
              />
            </Stack>
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Typography
                variant="caption"
                textAlign="right"
                color="text.secondary"
              >
                {trades.formattedCreatedAt}
              </Typography>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </Box>
  )
}

type AvatarWithNameProps = {
  reverse?: boolean
  user: Pick<User, 'displayName' | 'avatar' | 'id'> | null
}

const AvatarWithName: FC<AvatarWithNameProps> = ({ user, reverse = false }) => {
  return (
    <Stack
      direction={reverse ? 'row-reverse' : 'row'}
      alignItems="center"
      flex="1 1 0"
      spacing={2}
      sx={{ textDecoration: 'none', width: 'calc((100% - 40px) / 2)' }}
    >
      <Avatar
        src={user?.avatar?.smallUri ?? ''}
        alt={user?.displayName ?? ''}
        sx={{ width: 28, height: 28 }}
      />
      <Typography
        variant="overline"
        sx={{
          ...styleSets.lineCamp(1),
          maxWidth: '100%',
          textTransform: 'none',
        }}
      >
        {user?.displayName}
      </Typography>
    </Stack>
  )
}
